import { useMutation } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { TestDispatchInput, TestDispatchResult } from "../shared/communications-types";

interface TestDispatchParams {
  readonly communicationId: string;
  readonly input: TestDispatchInput;
}

/**
 * Fire an existing catalog communication to a real user over a forced channel
 * (API-COMM-5). Renders the versioned template server-side and delivers it.
 *
 * Returns a {@link TestDispatchResult} with HTTP `200` for every non-error
 * outcome — `SKIPPED_*` / `FAILED` are 200s that describe why nothing was
 * delivered, NOT thrown errors. Only a hard 4xx (bad id / malformed body) or a
 * 5xx surfaces as a thrown `ApiError`.
 *
 * No query invalidation: a test dispatch never mutates catalog state.
 */
async function testDispatch({
  communicationId,
  input,
}: TestDispatchParams): Promise<TestDispatchResult> {
  return httpClient<TestDispatchResult>(
    `/api/admin/communications/${communicationId}/test-dispatch`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
}

export function useTestDispatch() {
  return useMutation({ mutationFn: testDispatch });
}
